// frontend/src/pages/admin/ManagePaymentsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PaymentRequest, UserInfo } from '../../types'; // Ensure UserInfo is imported
import { toast } from 'react-toastify';

const ManagePaymentsPage: React.FC = () => {
    const { user } = useAuth();
    // Combined list for all payment requests, filtered by current selections
    const [allPaymentRequests, setAllPaymentRequests] = useState<PaymentRequest[]>([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [filterStatus, setFilterStatus] = useState<string>('pending'); // Default to pending
    const [filterType, setFilterType] = useState<string>('All'); // Default to all types
    const [searchTerm, setSearchTerm] = useState<string>(''); // For transaction ID or user name/email

    // Custom Tailwind animations via CSS (copied for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
    `;

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to view this page.');
            setLoading(false);
            return;
        }

        try {
            // Construct query parameters based on filter states
            const queryParams = new URLSearchParams();
            if (filterStatus && filterStatus !== 'All') {
                queryParams.append('status', filterStatus);
            }
            if (filterType && filterType !== 'All') {
                queryParams.append('type', filterType);
            }
            if (searchTerm) {
                queryParams.append('search', searchTerm);
            }

            // Always use the general /api/admin/payments endpoint with filters
            const url = `${process.env.REACT_APP_API_BASE_URL}/admin/payments?${queryParams.toString()}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch payment requests.');
            }

            const data: PaymentRequest[] = await response.json();
            setAllPaymentRequests(data);

        } catch (err: any) {
            toast.error(err.message || 'Error fetching payment requests.');
        } finally {
            setLoading(false);
        }
    }, [user, filterStatus, filterType, searchTerm]); // Depend on filter states to re-fetch

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleAction = useCallback(async (requestId: string, type: 'deposit' | 'withdrawal', action: 'approve' | 'reject') => {
        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to perform this action.');
            return;
        }

        // IMPORTANT: Replace window.confirm with a custom modal for better UX and consistency
        if (!window.confirm(`Are you sure you want to ${action} this ${type} request?`)) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/payments/${requestId}/${action}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to ${action} ${type} request.`);
            }

            toast.success(`${type} request ${action}ed successfully!`);
            fetchRequests(); // Re-fetch requests to update the lists

        } catch (err: any) {
            toast.error(err.message || `Error ${action}ing ${type} request.`);
        }
    }, [user, fetchRequests]);

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200 font-inter">
                <style>{customAnimations}</style>
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Loading payment requests...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (!user || !user.isAdmin) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Access Denied!</h2>
                    <p className="text-lg text-gray-700">You do not have administrative privileges to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-8 min-h-screen font-inter bg-gradient-to-br from-blue-100 to-purple-200">
            <style>{customAnimations}</style> {/* Inject custom animations */}

            <h1 className="text-4xl font-extrabold text-center mb-10 text-indigo-800 animate-fade-in-down">
                💸 Manage Payment Requests 💸
            </h1>

            {/* Filter Section */}
            <div className="bg-white p-6 rounded-2xl shadow-3xl mb-8 border-t-8 border-yellow-400 animate-zoom-in-out">
                <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">Filter & Search Requests</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                        <label htmlFor="filterStatus" className="block text-gray-700 text-lg font-bold mb-2">Filter by Status:</label>
                        <select
                            id="filterStatus"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="All">All Statuses</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="filterType" className="block text-gray-700 text-lg font-bold mb-2">Filter by Type:</label>
                        <select
                            id="filterType"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="All">All Types</option>
                            <option value="deposit">Deposit</option>
                            <option value="withdrawal">Withdrawal</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="searchTerm" className="block text-gray-700 text-lg font-bold mb-2">Search (Tx ID / User):</label>
                        <input
                            type="text"
                            id="searchTerm"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm"
                            placeholder="Transaction ID or User Name/Email"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <button
                    onClick={() => fetchRequests()} // Trigger fetch when filters change
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out shadow-lg text-xl transform hover:scale-105"
                >
                    Apply Filters
                </button>
            </div>

            {/* Combined Payment Requests List */}
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                All Payment Requests ({allPaymentRequests.length})
            </h2>
            {allPaymentRequests.length === 0 ? (
                <p className="text-center text-gray-600 text-lg py-8">No payment requests found matching the current filters. Adjust your filters or check back later!</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {allPaymentRequests.map(request => (
                        <div
                            key={request._id}
                            className={`relative bg-white p-6 rounded-xl shadow-lg border-t-4 transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl animate-fade-in-up
                                ${request.status === 'approved' ? 'border-green-500' :
                                   request.status === 'rejected' ? 'border-red-500' :
                                   'border-yellow-500' // Default for pending
                                }`}
                        >
                            {/* Status Badge */}
                            <div className={`absolute top-0 right-0 m-3 px-3 py-1 rounded-full text-white text-xs font-bold uppercase shadow-md
                                ${request.status === 'approved' ? 'bg-green-600' :
                                  request.status === 'rejected' ? 'bg-red-600' :
                                  'bg-yellow-600'
                                }`}>
                                {request.status}
                            </div>

                            <p className="text-2xl font-extrabold text-gray-800 mb-2 mt-2">Rs. {request.amount.toFixed(2)}</p>
                            <p className="text-md text-gray-700 mb-1">
                                <span className="font-semibold text-gray-800">Type:</span> <span className="capitalize">{request.type}</span>
                            </p>
                            <p className="text-md text-gray-700 mb-1">
                                <span className="font-semibold text-gray-800">User:</span> {(request.user as UserInfo)?.fullName || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600 mb-3">
                                <span className="font-semibold">Requested On:</span> {new Date(request.createdAt).toLocaleString()}
                            </p>

                            {/* Deposit-specific details */}
                            {request.type === 'deposit' && (
                                <div className="bg-blue-50 p-3 rounded-md mb-3">
                                    <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Method:</span> {request.method || 'N/A'}</p>
                                    {request.transactionId && <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Tx ID:</span> <span className="font-mono break-all">{request.transactionId}</span></p>}
                                    {request.receiptUrl && (
                                        <p className="text-gray-700 text-sm">
                                            <span className="font-semibold">Receipt:</span> <a href={request.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View Receipt</a>
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Withdrawal-specific details */}
                            {request.type === 'withdrawal' && (
                                <div className="bg-purple-50 p-3 rounded-md mb-3">
                                    <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Bank:</span> {request.bankName || 'N/A'}</p>
                                    <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Account:</span> {request.accountNumber || 'N/A'}</p>
                                    {request.accountHolderName && <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Account Holder:</span> {request.accountHolderName}</p>}
                                    {request.mobileNumber && <p className="text-gray-700 text-sm mb-1"><span className="font-semibold">Mobile:</span> {request.mobileNumber}</p>}
                                </div>
                            )}

                            {request.status === 'pending' && ( // Only show action buttons for pending requests
                                <div className="mt-4 flex flex-wrap gap-2 justify-end">
                                    <button
                                        onClick={() => handleAction(request._id, request.type, 'approve')}
                                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300 shadow-md transform hover:scale-105"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => handleAction(request._id, request.type, 'reject')}
                                        className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300 shadow-md transform hover:scale-105"
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManagePaymentsPage;
